import { SidePanel } from '@carbon/ibm-products';
import { Button, InlineNotification, Tile } from '@carbon/react';
import { Edit } from '@carbon/react/icons';
import { useAtomValue, useSetAtom } from 'jotai';
import { useState } from 'react';
import {
  EMPTY_BATCH_DEFAULT,
  batchDefaultAtom,
  curationRowsAtom,
  isBatchDefaultSet,
  setBatchDefaultAtom,
} from './atoms';
import styles from './bulk-upload.module.scss';
import { EntityAccountField } from './entity-account-field';
import { EM_DASH } from './fields';

/**
 * The batch default Insights entity and CRM account: required before Bulk
 * Create Assets (a warning above the card says so once there are rows), and
 * what every row without its own entity or account resolves against. Opens the
 * picker in a side panel; Apply is available once the entity has an account.
 * @return {JSX.Element} The card and its panel
 */
export const BatchDefaultCard = () => {
  const batchDefault = useAtomValue(batchDefaultAtom);
  const setBatchDefault = useSetAtom(setBatchDefaultAtom);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(EMPTY_BATCH_DEFAULT);
  const isSet = isBatchDefaultSet(batchDefault);
  const hasRows = useAtomValue(curationRowsAtom).length > 0;

  const close = () => setOpen(false);
  const choose = () => {
    setDraft(batchDefault);
    setOpen(true);
  };

  return (
    <>
      {hasRows && !isSet && (
        <InlineNotification
          kind="warning"
          lowContrast
          hideCloseButton
          title="Choose a default Insights Entity and CRM Account below."
          subtitle="Assets can be created once a default is chosen."
        />
      )}
      <Tile
        className={styles.defaultCard}
        role="region"
        aria-label="Default Insights Entity and CRM Account"
      >
        <div className={styles.defaultCardText}>
          <h3 className={styles.defaultCardTitle}>
            Default Insights Entity and CRM Account (Required)
          </h3>
          <p className={styles.defaultCardHelper}>
            Assets without their own entity or account are created with these.
          </p>
          <dl className={styles.defaultCardValues}>
            <dt>Insights Entity</dt>
            <dd>
              {batchDefault.entityName ??
                (batchDefault.entityId === null
                  ? EM_DASH
                  : String(batchDefault.entityId))}
            </dd>
            <dt>CRM Account</dt>
            <dd>
              {batchDefault.accountName ?? (batchDefault.accountId || EM_DASH)}
            </dd>
          </dl>
        </div>
        <Button
          kind={isSet ? 'tertiary' : 'primary'}
          size="md"
          renderIcon={Edit}
          onClick={choose}
        >
          {isSet ? 'Change default' : 'Choose default'}
        </Button>
        <SidePanel
          open={open}
          onRequestClose={close}
          title="Default Insights Entity and CRM Account"
          subtitle="Choose an entity, then one of its CRM accounts, or link or create one."
          slideIn
          selectorPageContent="#main-content"
          placement="right"
          size="xl"
          actions={[
            { kind: 'secondary', label: 'Cancel', onClick: close },
            {
              kind: 'primary',
              label: 'Apply',
              disabled: !isBatchDefaultSet(draft),
              onClick: () => {
                setBatchDefault(draft);
                close();
              },
            },
          ]}
        >
          {open && <EntityAccountField onChange={setDraft} />}
        </SidePanel>
      </Tile>
    </>
  );
};
